/*************************************************************************
 *
 *  Copyright 2020 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
/* Circular Doubly Linked List Utils */
class Node {
  constructor(key, value) {
    this.next = this;
    this.previous = this;
    this[key] = value;
  }
}

class LinkedList {
  constructor() {
    this.head = null;
    this.length = 0;
  }
  getHead() {
    return this.head;
  }

  get tail() {
    return this.head && this.head.previous;
  }

  getTail() {
    return this.tail;
  }

  getLength() {
    return this.length;
  }

  insertNodeAtBeginning(node) {
    this.insertNodeAtEnd(node);
    this.head = node;
  }

  insertNodeAtEnd(node) {
    if (!(node instanceof Node)) {
      // this covers null/undefined checks as well
      throw new Error("Linked List node must be a Node object");
    }

    if (this.head === null) {
      this.head = node;
    } else {
      node.next = this.head;
      node.previous = this.tail;
      this.tail.next = node;
      this.head.previous = node;
    }
    this.length += 1;
  }

  removeNode(node) {
    // assumes node is part of the linkedList.
    if (node && this.length) {
      if (this.length === 1) {
        this.head = null;
      } else {
        node.previous.next = node.next;
        node.next.previous = node.previous;
        if (node === this.head) {
          this.head = node.next;
        }
      }

      this.length -= 1;
    }
  }

  deleteAll(deletor) {
    if (this.length === 0) return;
    let listIterator = this.head;
    let next;
    let i = 0;
    while (i < this.length) {
      if (deletor(listIterator)) {
        next = listIterator.next;
        this.removeNode(listIterator);
        listIterator = next;
        i -= 1;
      } else {
        listIterator = listIterator.next;
      }
      i += 1;
    }
  }

  forEach(callback) {
    if (this.length > 0) {
      let listIterator = this.head;
      let i = 0;
      while (i < this.length) {
        callback(listIterator);
        listIterator = listIterator.next;
        i += 1;
      }
    }
  }
}

export { Node, LinkedList };
